import * as Sentry from '@sentry/react'
import {
  type QueryClient,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect } from 'react'
import { userQueryOptions } from '@/core/auth/user-query'
import type { UserQuery } from '@/generated/graphql'
import { configQueryOptions } from './use-config'

// `GET /v1/config` is where the server runs the start-flow split decision, and
// it is itself gated on the "me" query succeeding (use-config.ts:33,
// feature-flags-prefetch.tsx:20-23). So the cached user of a session always
// predates the decision it is meant to carry: it reports no start flow, nothing
// renders from it, and anything else computed from it acts on a verdict it
// cannot see.

// Keyed by the decision's own stamp rather than by "user newer than config":
// those two land in the same millisecond often enough that the comparison would
// silently drop reads. The cache entry is the latch, so one read serves every
// observer and a remount finds it done instead of repeating it.
function readOptions(decidedAt: number, queryClient: QueryClient) {
  return {
    queryKey: ['post-decision-user', decidedAt] as const,
    // staleTime:0 is load-bearing — the client default is 5 minutes, under
    // which this would hand back the pre-decision copy already in cache. It
    // writes through the shared ['user'] entry, so every consumer of the user
    // query reads the post-decision copy without asking for one.
    queryFn: () =>
      queryClient.fetchQuery({ ...userQueryOptions, staleTime: 0 }),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  }
}

// Issues the read. Mounted by FeatureFlagsPrefetch, which already owns /config,
// so the decide → read ordering is a property of the app shell: it holds when
// no feature asks for it, and no feature hook can relocate or delete it.
export function usePostDecisionUserRead(): void {
  const queryClient = useQueryClient()
  // Observed, never issued: FeatureFlagsPrefetch drives /config itself.
  const config = useQuery({ ...configQueryOptions, enabled: false })
  const decidedAt = config.isFetched ? config.dataUpdatedAt : 0
  const read = useQuery({
    ...readOptions(decidedAt, queryClient),
    enabled: decidedAt !== 0,
  })

  useEffect(() => {
    if (!read.error) return
    Sentry.captureException(read.error, {
      tags: { feature: 'feature-flags', stage: 'postDecisionUser' },
    })
  }, [read.error])

  // A /config that never landed leaves no decision to read, so nothing
  // downstream of it runs and the user drops out of the experiment in silence.
  // The direction is safe — nothing is spent — but the sample loses users at
  // whatever rate /config fails, so make that rate a number somebody can look
  // up rather than an unexplained shortfall during analysis.
  useEffect(() => {
    if (!config.isError) return
    Sentry.captureMessage('Start-flow decision unavailable on /config', {
      level: 'warning',
      tags: { feature: 'feature-flags', stage: 'postDecisionUser' },
    })
  }, [config.isError])
}

// Reads what that read produced. Undefined until it lands, which is the safe
// direction: no user, no action, and the server's decision is still there to be
// served on the next load. Pure observer — with the shell's read gone this
// never resolves, so a caller cannot accidentally act on the pre-decision copy.
export function usePostDecisionUser(): UserQuery['me'] | undefined {
  const queryClient = useQueryClient()
  const config = useQuery({ ...configQueryOptions, enabled: false })
  const cached = useQuery({ ...userQueryOptions, enabled: false })
  const decidedAt = config.isFetched ? config.dataUpdatedAt : 0
  const read = useQuery({
    ...readOptions(decidedAt, queryClient),
    enabled: false,
  })

  // The shared ['user'] entry rather than the read's own copy, so a later
  // refetch of the user is still seen once this decision's read has landed.
  return read.isSuccess ? cached.data?.me : undefined
}

// True once that read has landed or can no longer land.
export function usePostDecisionReadSettled(): boolean {
  const queryClient = useQueryClient()
  const config = useQuery({ ...configQueryOptions, enabled: false })
  const decidedAt = config.isFetched ? config.dataUpdatedAt : 0
  const read = useQuery({
    ...readOptions(decidedAt, queryClient),
    enabled: false,
  })
  return config.isError || read.isSuccess || read.isError
}
